import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useMarketplaceOrder, useMarketplaceOrders } from '../../../hooks/useMarketplace';
import useAuthStore from '../../../store/useAuthStore';

const MarketplaceOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { order, loading, refetch } = useMarketplaceOrder(orderId);
  const { sellerConfirmDelivery, buyerConfirmReceipt, cancelOrder } = useMarketplaceOrders();
  const { user } = useAuthStore();
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(''); // 'delivery' or 'receipt'
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-white';
      case 'negotiating': return 'text-blue-400';
      case 'asset_delivered': return 'text-cyan-400';
      case 'asset_received': return 'text-purple-400';
      case 'admin_review': return 'text-orange-400';
      case 'completed': return 'text-green-400';
      case 'cancelled': return 'text-red-400';
      case 'refunded': return 'text-white';
      default: return 'text-white';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Order Created - BTC1 in Escrow';
      case 'negotiating': return 'Negotiating';
      case 'asset_delivered': return 'Seller Confirmed Delivery';
      case 'asset_received': return 'Buyer Confirmed Receipt';
      case 'admin_review': return 'Awaiting Admin Approval';
      case 'completed': return 'Completed - BTC1 Released';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      case 'disputed': return 'Disputed';
      default: return status;
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-6 w-6" />;
      case 'cancelled':
      case 'refunded': return <XCircleIcon className="h-6 w-6" />;
      case 'admin_review': return <ExclamationTriangleIcon className="h-6 w-6" />;
      default: return <ClockIcon className="h-6 w-6" />;
    }
  };
  
  const handleConfirmDelivery = async () => {
    setIsProcessing(true);
    try {
      await sellerConfirmDelivery(orderId, notes);
      setShowConfirmModal(false);
      setNotes('');
      refetch();
    } catch (error) {
      console.error('Error confirming delivery:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleConfirmReceipt = async () => {
    setIsProcessing(true);
    try {
      await buyerConfirmReceipt(orderId, notes);
      setShowConfirmModal(false);
      setNotes('');
      refetch();
    } catch (error) {
      console.error('Error confirming receipt:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? BTC1 will be refunded.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await cancelOrder(orderId);
      refetch();
    } catch (error) {
      console.error('Error cancelling order:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Order not found</p>
          <button
            onClick={() => navigate('/p2p')}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    );
  }
  
  const isUserBuyer = user?.username === order.buyer_profile?.user?.username || user?.email === order.buyer_profile?.user?.email;
  const isUserSeller = user?.username === order.seller_profile?.user?.username || user?.email === order.seller_profile?.user?.email;
  
  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/p2p')}
          className="mb-6 flex items-center space-x-2 text-white hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Back to Marketplace</span>
        </button>
        
        {/* Header */}
        <div className="bg-[#010615] rounded-xl border border-gray-700 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{order.asset.title}</h1>
              <p className="text-white">Order ID: <span className="font-mono">{order.order_id.toString().slice(0, 8)}</span></p>
            </div>
            
            <div className={`flex items-center space-x-2 ${getStatusColor(order.status)}`}>
              {getStatusIcon(order.status)}
              <span className="font-medium">{getStatusText(order.status)}</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Order Details */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-bold mb-4">Order Details</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-white">Asset</span>
                  <span className="font-medium">{order.asset.title}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-white">Category</span>
                  <span className="font-medium capitalize">{order.asset.category}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-white">Price</span>
                  <span className="font-medium">{parseFloat(order.btc_amount).toFixed(4)} BTC1</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-white">USD Value</span>
                  <span className="font-medium">${parseFloat(order.usd_value).toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-white">Your Role</span>
                  <span className="font-medium">{isUserBuyer ? 'Buyer' : isUserSeller ? 'Seller' : 'Unknown'}</span>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-bold mb-4">
                {isUserBuyer ? 'Seller' : 'Buyer'} Contact
              </h2>
              
              {order.asset.contact_info && (
                <div className="bg-gray-800 rounded-lg p-4">
                  <pre className="text-white whitespace-pre-wrap text-sm">
                    {order.asset.contact_info}
                  </pre>
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            {/* Status Timeline */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-bold mb-4">Order Progress</h2>
              
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    order.status !== 'cancelled' ? 'bg-green-600' : 'bg-gray-700'
                  }`}>
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Order Created</p>
                    <p className="text-sm text-white">BTC1 locked in escrow</p>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    order.seller_confirmed_delivery ? 'bg-green-600' : 'bg-gray-700'
                  }`}>
                    {order.seller_confirmed_delivery ? (
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                    ) : (
                      <ClockIcon className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Seller Confirms Delivery</p>
                    {order.seller_confirmed_at && (
                      <p className="text-sm text-white">
                        {new Date(order.seller_confirmed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Step 3 */}
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    order.buyer_confirmed_receipt ? 'bg-green-600' : 'bg-gray-700'
                  }`}>
                    {order.buyer_confirmed_receipt ? (
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                    ) : (
                      <ClockIcon className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Buyer Confirms Receipt</p>
                    {order.buyer_confirmed_at && (
                      <p className="text-sm text-white">
                        {new Date(order.buyer_confirmed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Step 4 */}
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    order.admin_reviewed ? 'bg-green-600' : 'bg-gray-700'
                  }`}>
                    {order.admin_reviewed ? (
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                    ) : (
                      <ClockIcon className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Admin Approval</p>
                    {order.admin_reviewed_at ? (
                      <p className="text-sm text-white">
                        {new Date(order.admin_reviewed_at).toLocaleString()}
                      </p>
                    ) : order.status === 'admin_review' ? (
                      <p className="text-sm text-white">Pending review...</p>
                    ) : null}
                  </div>
                </div>
                
                {/* Step 5 */}
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    order.status === 'completed' ? 'bg-green-600' : 'bg-gray-700'
                  }`}>
                    {order.status === 'completed' ? (
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                    ) : (
                      <ClockIcon className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">BTC1 Released</p>
                    {order.completed_at && (
                      <p className="text-sm text-white">
                        {new Date(order.completed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="bg-[#010615] rounded-xl border border-gray-700 p-6">
              <h2 className="text-xl font-bold mb-4">Actions</h2>
              
              <div className="space-y-3">
                {/* Seller: Confirm Delivery */}
                {order.can_seller_confirm && (
                  <button
                    onClick={() => {
                      setConfirmType('delivery');
                      setShowConfirmModal(true);
                    }}
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    ✅ Confirm Asset Delivered
                  </button>
                )}
                
                {/* Buyer: Confirm Receipt */}
                {order.can_buyer_confirm && (
                  <button
                    onClick={() => {
                      setConfirmType('receipt');
                      setShowConfirmModal(true);
                    }}
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-green-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    ✅ Confirm Asset Received
                  </button>
                )}
                
                {/* Cancel Button */}
                {order.can_cancel && (
                  <button
                    onClick={handleCancel}
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    Cancel Order
                  </button>
                )}
                
                {/* Status Messages */}
                {order.status === 'admin_review' && (
                  <div className="bg-yellow-900/20 border border-[#2A356D]/30 rounded-lg p-4">
                    <p className="text-white text-sm">
                      ⏳ Both parties have confirmed. Waiting for admin to release BTC1...
                    </p>
                  </div>
                )}
                
                {order.status === 'completed' && (
                  <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
                    <p className="text-green-400 text-sm">
                      ✅ Order completed! BTC1 has been {isUserSeller ? 'released to you' : 'released to seller'}.
                    </p>
                  </div>
                )}
                
                {order.status === 'asset_delivered' && isUserBuyer && (
                  <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
                    <p className="text-blue-400 text-sm">
                      📦 Seller confirmed delivery. Please confirm once you receive the asset.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {confirmType === 'delivery' ? 'Confirm Delivery' : 'Confirm Receipt'}
            </h3>
            
            <p className="text-white mb-4">
              {confirmType === 'delivery' 
                ? 'Confirm that you have delivered the asset to the buyer.'
                : 'Confirm that you have received the asset from the seller.'}
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes or details..."
                rows={3}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              
              <button
                onClick={confirmType === 'delivery' ? handleConfirmDelivery : handleConfirmReceipt}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                {isProcessing ? 'Confirming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceOrderDetail;

